import { Link as RouterLink } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { colorTokens, radiusTokens, typographyTokens } from '../app/designTokens';
import { SectionCard } from './SectionCard';

const { semantic } = colorTokens;

/**
 * Clickable metric / navigation surface for dashboards.
 * Large value on top, muted label below, optional helper under the label.
 * Pass helper="" to reserve equal helper space without visible text.
 * Uses Interactive SectionCard when linked.
 */
export function MetricCard({ value, label, helper, to, onClick }) {
  const hasHelperSlot = helper !== undefined;

  const content = (
    <SectionCard
      variant={to || onClick ? 'Interactive' : 'Default'}
      onClick={to ? undefined : onClick}
      sx={{
        mb: 0,
        height: '100%',
        minHeight: hasHelperSlot ? 128 : 104,
        overflow: 'visible',
        display: 'flex',
        flexDirection: 'column',
        ...(to || onClick
          ? {
              '&:hover': {
                borderColor: semantic.border.default,
                bgcolor: semantic.action.secondary.bg,
              },
            }
          : null),
        '& > :last-child': {
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <Typography
        component="p"
        sx={{
          m: 0,
          mb: 1,
          fontFamily: typographyTokens.fontFamilyDisplay,
          fontWeight: typographyTokens.fontWeightBold,
          fontSize: { xs: 22, md: 28 },
          lineHeight: { xs: '28px', md: '34px' },
          letterSpacing: '-0.4px',
          color: semantic.text.primary,
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
      <Typography
        sx={{
          fontFamily: typographyTokens.fontFamily,
          fontSize: 12,
          lineHeight: '16px',
          letterSpacing: '0.24px',
          color: semantic.text.muted,
        }}
      >
        {label}
      </Typography>
      {hasHelperSlot ? (
        <Typography
          sx={{
            mt: 'auto',
            pt: 1.25,
            minHeight: 32,
            fontFamily: typographyTokens.fontFamily,
            fontSize: 12,
            lineHeight: '16px',
            color: semantic.text.muted,
            visibility: helper ? 'visible' : 'hidden',
          }}
        >
          {helper || '\u00a0'}
        </Typography>
      ) : (
        <Box sx={{ flex: 1, minHeight: 4 }} />
      )}
    </SectionCard>
  );

  if (to) {
    return (
      <Box
        component={RouterLink}
        to={to}
        sx={{
          display: 'block',
          height: '100%',
          textDecoration: 'none',
          color: 'inherit',
          borderRadius: `${radiusTokens.card}px`,
          '&:focus-visible': {
            outline: `2px solid ${semantic.status.brand.border}`,
            outlineOffset: 2,
          },
        }}
      >
        {content}
      </Box>
    );
  }

  return content;
}
